import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, HelpCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import SEOHead from '../components/seo/SEOHead';
import Breadcrumbs from '../components/seo/Breadcrumbs';
import InternalLinks from '../components/seo/InternalLinks';
import { JsonLd, faqSchema } from '../components/seo/schemas';

const FAQ = () => {
  const faqCategories = [
    {
      category: "Getting Started",
      faqs: [
        {
          question: "What is the cost of setting up a mineral water plant?",
          answer: "The cost varies based on capacity and automation level:\n\n• Basic 1000 BPH plant: ₹25-30 lakhs\n• Semi-automatic 3000 BPH: ₹50-70 lakhs\n• Fully automatic 6000 BPH: ₹80 lakhs - 1.5 crores\n• Large scale 12000 BPH: ₹1.5-2.5 crores\n\nThis includes complete turnkey setup: RO system, filling line, packaging equipment, and installation. Contact us for a detailed quotation based on your specific requirements."
        },
        {
          question: "How long does it take to install a complete beverage plant?",
          answer: "Our typical project timeline is 3-6 months from order confirmation to production-ready plant:\n\n• Planning & Design: 2-3 weeks\n• Manufacturing: 6-10 weeks\n• Installation & Setup: 3-4 weeks\n• Trial Production & Training: 1 week\n\nTimeline depends on plant capacity, customization requirements, and site readiness. We provide a detailed project schedule during the consultation phase."
        },
        {
          question: "Do you help beginners who have no experience in beverage manufacturing?",
          answer: "Absolutely! We specialize in helping entrepreneurs start their beverage business from scratch:\n\n✓ Business feasibility guidance\n✓ Complete plant setup\n✓ License and compliance support\n✓ Comprehensive operator training\n✓ Production process guidance\n✓ Quality control procedures\n✓ Ongoing technical support\n\nMany of our clients were first-time entrepreneurs who successfully started their beverage businesses with our complete guidance."
        },
        {
          question: "What licenses are required to start a beverage plant?",
          answer: "Key licenses and registrations required:\n\n1. FSSAI License (Food Safety Standards Authority of India)\n2. BIS Certification (Bureau of Indian Standards) - Mandatory for packaged drinking water\n3. Pollution Control Board Clearance (PCB)\n4. Trade License from Local Municipal Authority\n5. GST Registration\n6. Factory License (for larger operations)\n\nWe provide complete documentation support and guidance for obtaining all necessary licenses. Our team assists with application preparation and compliance requirements."
        }
      ]
    },
    {
      category: "Plant Setup & Technology",
      faqs: [
        {
          question: "Can you customize the plant according to our specific needs?",
          answer: "Yes, customization is our specialty! We design plants based on:\n\n• Your production capacity requirements\n• Available space and layout\n• Budget constraints\n• Specific product formulations\n• Automation level preferences\n• Future expansion plans\n\nEvery project is tailored to client requirements. We don't believe in one-size-fits-all solutions."
        },
        {
          question: "What is the difference between RO water and mineral water?",
          answer: "Key differences:\n\n**RO (Packaged Drinking) Water:**\n• Purified through Reverse Osmosis\n• Removes all impurities and minerals\n• Requires BIS certification\n• Lower operating costs\n• Suitable for general consumption\n\n**Mineral Water:**\n• Contains naturally occurring minerals OR minerals added after purification\n• Requires source approval or mineral dosing system\n• Premium product positioning\n• Specific BIS standards\n\nBoth require proper treatment and BIS certification for commercial bottling. We provide complete solutions for both types."
        },
        {
          question: "What capacity should I choose for my plant?",
          answer: "Capacity selection depends on:\n\n**For Mineral Water:**\n• 1000-2000 BPH: Local market, small investment\n• 3000-4000 BPH: District-level distribution\n• 6000-8000 BPH: Multi-district supply\n• 10000+ BPH: State-level or large contracts\n\n**Factors to Consider:**\n• Market demand analysis\n• Investment capacity\n• Distribution reach\n• Competition\n• Future growth plans\n\nWe help you choose the optimal capacity during our feasibility consultation."
        },
        {
          question: "Do you provide training for plant operators?",
          answer: "Yes, comprehensive training is included in all our turnkey projects:\n\n**Training Covers:**\n• Plant operation procedures\n• Quality control & testing\n• Maintenance guidelines\n• Troubleshooting common issues\n• Safety protocols\n• Hygiene standards\n• Record keeping\n\n**Training Duration:** 5-7 days hands-on training\n**Trainers:** Experienced technical staff\n**Materials:** Complete operation manuals in English/Hindi\n**Support:** Ongoing phone/video support after training"
        }
      ]
    },
    {
      category: "Cost & Investment",
      faqs: [
        {
          question: "What is included in the turnkey project cost?",
          answer: "Our turnkey package includes:\n\n**Equipment & Machinery:**\n✓ RO/Water treatment system\n✓ Filling & capping machines\n✓ Conveyor systems\n✓ Packaging equipment\n✓ Storage tanks\n✓ Quality testing lab equipment\n\n**Installation Services:**\n✓ Complete plant installation\n✓ Piping & utilities setup\n✓ Electrical integration\n✓ Automation systems\n\n**Additional Services:**\n✓ Plant layout & design\n✓ Trial production\n✓ Operator training\n✓ Documentation\n✓ 1-year warranty\n\n**Not Included:** Land, building construction, raw materials, licenses (we provide support)."
        },
        {
          question: "Do you offer financing or payment plans?",
          answer: "Payment structure typically follows project milestones:\n\n• 30% - Order confirmation (advance)\n• 40% - During manufacturing (progress payment)\n• 25% - Before dispatch\n• 5% - After installation & commissioning\n\nFor financing options:\n• We provide project reports for bank loans\n• Assistance with MUDRA/PMEGP loans for eligible entrepreneurs\n• Can connect with financing partners\n• Documentation support for subsidy schemes\n\nMany banks offer attractive rates for food processing/beverage projects. We help prepare loan applications."
        },
        {
          question: "What is the expected ROI (Return on Investment)?",
          answer: "ROI varies based on several factors, but typical scenarios:\n\n**Mineral Water Plant (6000 BPH):**\n• Investment: ₹80 lakhs - 1 crore\n• Monthly Revenue Potential: ₹15-25 lakhs\n• Profit Margin: 25-35%\n• ROI Period: 18-24 months\n\n**Factors Affecting ROI:**\n✓ Market pricing\n✓ Distribution efficiency\n✓ Operational costs\n✓ Brand positioning\n✓ Production utilization\n\nWe provide detailed ROI calculations during consultation based on your market conditions."
        }
      ]
    },
    {
      category: "After-Sales & Support",
      faqs: [
        {
          question: "What after-sales support do you provide?",
          answer: "Comprehensive post-installation support:\n\n**Warranty:**\n• 1-year comprehensive warranty on all equipment\n• Extended AMC options available\n\n**Technical Support:**\n• 24/7 phone support\n• Video call troubleshooting\n• On-site visits when required\n• Remote diagnostics (where applicable)\n\n**Spare Parts:**\n• Ready stock of critical spares\n• Fast dispatch (24-48 hours)\n• Competitive pricing\n• Genuine OEM parts\n\n**Upgrades & Expansion:**\n• Capacity enhancement options\n• Technology upgrades\n• New product line additions\n\nOur relationship doesn't end at installation - we're your long-term partner."
        },
        {
          question: "How do you handle maintenance and breakdowns?",
          answer: "Proactive maintenance approach:\n\n**Preventive Maintenance:**\n• Detailed maintenance schedules\n• Regular checkup reminders\n• Maintenance training for your staff\n\n**Breakdown Support:**\n• Call our support hotline: +91-9351292512\n• Initial diagnosis over phone/video\n• Spare parts dispatch within 24-48 hours\n• On-site technician visit if required\n• Most issues resolved within 72 hours\n\n**Annual Maintenance Contract (AMC):**\n• Scheduled visits\n• Preventive checks\n• Priority spare parts supply\n• Discounted on-site visits\n\nWe aim for maximum uptime to ensure your business runs smoothly."
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen">
      <SEOHead
        title="FAQs — Mineral Water Plant Cost, Setup & Licensing | Alden Technologies"
        description="Answers to common questions about mineral water plant cost, machinery, BIS/FSSAI licensing, space requirements, ROI and after-sales support from Alden Technologies."
        path="/faq"
      />
      <Breadcrumbs items={[{ label: 'FAQ' }]} />
      <JsonLd data={faqSchema(faqCategories.flatMap((c) => c.faqs))} />
      {/* Hero Section */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40"
            alt="FAQ Background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-900/90 to-emerald-700/70"></div>
        </div>
        
        <div className="container mx-auto px-4 z-10 relative text-center">
          <HelpCircle className="w-16 h-16 text-emerald-300 mx-auto mb-6" />
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Frequently Asked Questions
          </h1>
          <p className="text-xl md:text-2xl text-emerald-100 max-w-3xl mx-auto">
            Everything you need to know about starting your beverage plant
          </p>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {faqCategories.map((category) => (
              <div key={category.category} className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                  <span className="w-2 h-8 bg-emerald-600 rounded"></span>
                  {category.category}
                </h2>
                
                <Accordion type="single" collapsible className="space-y-4">
                  {category.faqs.map((faq) => (
                    <AccordionItem 
                      key={faq.question} 
                      value={`item-${category.category}-${faq.question}`}
                      className="border-2 border-gray-200 rounded-lg px-6 bg-white hover:border-emerald-500 transition-colors"
                    >
                      <AccordionTrigger className="text-left text-lg font-semibold text-gray-900 hover:text-emerald-600 py-6">
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-gray-700 leading-relaxed pt-2 pb-6 whitespace-pre-line">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Still Have Questions */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Still Have Questions?
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              Our team is here to help you with any queries about starting your beverage plant
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact">
                <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-6 text-lg">
                  Schedule Free Consultation
                  <ArrowRight className="ml-2" />
                </Button>
              </Link>
              <a href="tel:+919351292512">
                <Button size="lg" variant="outline" className="border-2 border-emerald-600 text-emerald-600 hover:bg-emerald-50 px-8 py-6 text-lg">
                  Call: +91-9351292512
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-emerald-600 to-emerald-800">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Start Your Beverage Business?
          </h2>
          <p className="text-xl text-emerald-100 mb-8 max-w-2xl mx-auto">
            Get expert guidance and a detailed project plan customized for your needs
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-emerald-600 hover:bg-gray-100 px-10 py-7 text-xl font-semibold">
              Get Free Project Consultation
              <ArrowRight className="ml-2" />
            </Button>
          </Link>
        </div>
      </section>
      <InternalLinks currentPath="/faq" />
    </div>
  );
};

export default FAQ;
